import { User } from '../../types';
import { hasUnlimitedTokens } from '../../utils/promoEffectsManager';
import { getTokenConfig } from './config';
import { TokenChargeRequest, TokenChargeResult, calculateCharge } from './pricing';

export interface TokenTransaction {
    id: string;
    userId: string;
    timestamp: number;
    operationType: string;
    charged: number;
    breakdown: TokenChargeResult['breakdown'];
    metadata: {
        model: string;
        complexity: string;
        features: string[];
    };
}

// State for transactions
let transactions: TokenTransaction[] = [];

/**
 * Load transactions from localStorage
 */
const loadTransactions = (): void => {
    try {
        const stored = localStorage.getItem('token_transactions');
        if (stored) {
            transactions = JSON.parse(stored);
            console.log(`[TokenSystem] Loaded ${transactions.length} transactions`);
        }
    } catch (error) {
        console.error('[TokenSystem] Failed to load transactions:', error);
    }
};

/**
 * Save transactions to localStorage
 */
const saveTransactions = (): void => {
    try {
        localStorage.setItem('token_transactions', JSON.stringify(transactions.slice(-1000)));
    } catch (error) {
        console.error('[TokenSystem] Failed to save transactions:', error);
    }
};

// Initialize transactions
loadTransactions();

/**
 * Record transaction for analytics
 */
const recordTransaction = (transaction: TokenTransaction): void => {
    transactions.push(transaction);

    // Keep only last 1000 transactions in memory
    if (transactions.length > 1000) {
        transactions = transactions.slice(-1000);
    }

    // Save to localStorage
    saveTransactions();
};

/**
 * Charge tokens to user
 * Skips charging if user has unlimited tokens
 */
export const chargeTokens = async (
    request: TokenChargeRequest,
    user: User,
    updateUser: (updates: Partial<User>) => void,
    skipWarnings: boolean = false
): Promise<TokenChargeResult> => {
    // CRITICAL: Check for unlimited tokens FIRST before any calculations
    const userHasUnlimited = hasUnlimitedTokens(user);

    if (userHasUnlimited) {
        console.log('[TokenSystem] User has UNLIMITED tokens - returning zero-charge result');
        // Return a guaranteed zero-charge result for unlimited users
        return {
            charged: 0,
            breakdown: {
                base: 0,
                complexity: 0,
                model: 0,
                attachments: 0,
                features: 0,
                subtotal: 0,
                discount: 0,
                total: 0,
            },
            remainingTokens: Infinity,
            dailyUsage: user.dailyTokenUsage || 0, // Don't touch their usage
            dailyLimit: Infinity,
            canProceed: true,
            warnings: [],
        };
    }

    const result = calculateCharge(request, user);

    if (!result.canProceed) {
        console.warn('[TokenSystem] Cannot proceed with charge:', result.warnings);
        return result;
    }

    // SAFETY CHECK: Don't update if charge is 0
    if (result.charged <= 0) {
        console.log('[TokenSystem] Zero charge - not updating usage');
        return result;
    }

    // Update user's token usage
    const newUsage = (user.dailyTokenUsage || 0) + result.charged;
    updateUser({ dailyTokenUsage: newUsage });

    // Record transaction
    recordTransaction({
        id: `tx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        userId: user.id,
        timestamp: Date.now(),
        operationType: request.operationType,
        charged: result.charged,
        breakdown: result.breakdown,
        metadata: {
            model: request.model,
            complexity: request.complexity,
            features: Object.keys(request.features).filter(k => request.features[k as keyof typeof request.features]),
        },
    });

    console.log(`[TokenSystem] Charged ${result.charged} tokens to ${user.username}`, result.breakdown);

    return result;
};

/**
 * Refund tokens to user
 */
export const refundTokens = async (
    amount: number,
    reason: 'error' | 'partial' | 'manual',
    user: User,
    updateUser: (updates: Partial<User>) => void
): Promise<void> => {
    const config = getTokenConfig();

    if (!config.refunds.enabled && reason !== 'manual') {
        console.log('[TokenSystem] Refunds disabled');
        return;
    }

    let refundAmount = amount;

    // Apply refund percentage
    if (reason === 'error') {
        refundAmount = Math.ceil(amount * (config.refunds.errorRefundPercent / 100));
    } else if (reason === 'partial') {
        refundAmount = Math.ceil(amount * (config.refunds.partialResponsePercent / 100));
    }

    const newUsage = Math.max(0, user.dailyTokenUsage - refundAmount);
    updateUser({ dailyTokenUsage: newUsage });

    console.log(`[TokenSystem] Refunded ${refundAmount} tokens to ${user.username} (reason: ${reason})`);

    // Record refund transaction
    recordTransaction({
        id: `refund_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        userId: user.id,
        timestamp: Date.now(),
        operationType: `refund_${reason}`,
        charged: -refundAmount,
        breakdown: {
            base: 0,
            complexity: 0,
            model: 0,
            attachments: 0,
            features: 0,
            subtotal: 0,
            discount: 0,
            total: -refundAmount,
        },
        metadata: {
            model: 'N/A',
            complexity: 'N/A',
            features: [reason],
        },
    });
};

/**
 * Get user's transaction history
 */
export const getTransactionHistory = (userId: string, limit: number = 50): TokenTransaction[] => {
    return transactions
        .filter(tx => tx.userId === userId)
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, limit);
};

/**
 * Get all transactions (internal use for analytics)
 */
export const getAllTransactions = (): TokenTransaction[] => {
    return transactions;
};
